import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * UserInsightsPage Component
 * 
 * Displays user insights tools content like the legacy HTML site
 */
const UserInsightsPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // User Insights intro content from the legacy HTML
  const userInsightsIntroHtml = `
    <p>
    Once you've started gathering insights from user interviews, there are several complementary methods to gather additional user insights. These tools help you understand users' behaviors, interactions, and mental models more deeply.
    </p>

    <p>
    User insights tools often provide different perspectives and can help validate or challenge the findings from interviews. They're particularly useful for understanding how users interact with existing products or competitors.
    </p>

    <p>
    The following tools offer different approaches to collecting user insights beyond traditional interviews.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const competitorUsabilityTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-insights/competitor-usability.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_klAHEUU=/" className="link-table">Miro</a>
    </>
  );

  const contextualInquiryTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-insights/contextual-inquiry.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_klP8qr0=/" className="link-table">Miro</a>
    </>
  );

  const cardSortingTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-insights/card-sorting.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_klRh6OU=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const naCreator = <a href="">N/A</a>;
  const holtzblattCreator = <a href="https://en.wikipedia.org/wiki/Karen_Holtzblatt">Karen Holtzblatt & Hugh Beyer</a>;
  const donaFarrellCreator = <a href="https://www.nngroup.com/articles/card-sorting-definition/">Dona Farrell</a>;

  return (
    <>
      {/* User Insights Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/research#empathise">Empathise</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research/empathise/user-insights">User Insights</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={userInsightsIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#competitor-usability">Competitor Usability</Link></div>
            <div><Link className="tool-section-cta" to="#contextual-inquiry">Contextual Inquiry</Link></div>
            <div><Link className="tool-section-cta" to="#card-sorting">Card Sorting</Link></div>
          </div>
        </div>
      </div>

      {/* Competitor Usability Section */}
      <main className="case-study">
        <span className="case-tool-title" id="competitor-usability">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="competitor-usability">
            <span className="tool-title-number">
              <Link to="/empathise/user-insights#competitor-usability">Competitor Usability</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-insights">User Insights</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-insights#competitor-usability"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-competitor-usability" 
            id="tab-competitor-usability-overview" 
            defaultChecked
          />
          <label htmlFor="tab-competitor-usability-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Insights", "18/08/20", competitorUsabilityTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                Competitor Usability involves observing users as they interact with competitors' products. This helps identify both pain points and positive experiences in existing solutions.
                </p>
                
                <p>
                By conducting usability tests on competitor products, you can gain insights into users' expectations, common patterns in the industry, and opportunities for differentiation or improvement.
                </p>
                
                <p>
                This approach is particularly valuable because it allows you to learn from others' successes and failures without investing in building your own solutions first.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/competitive-usability-evaluations/" className="link-table">Competitive Usability Evaluations</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.usability.gov/how-to-and-tools/methods/competitive-analysis.html" className="link-table">Competitive Analysis - Usability.gov</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-competitor-usability" 
            id="tab-competitor-usability-template"
          />
          <label htmlFor="tab-competitor-usability-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_klAHEUU=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Competitor Usability Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Contextual Inquiry Section */}
      <main className="case-study">
        <span className="case-tool-title" id="contextual-inquiry">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="contextual-inquiry">
            <span className="tool-title-number">
              <Link to="/empathise/user-insights#contextual-inquiry">Contextual Inquiry</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-insights">User Insights</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-insights#contextual-inquiry"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-contextual-inquiry" 
            id="tab-contextual-inquiry-overview" 
            defaultChecked
          />
          <label htmlFor="tab-contextual-inquiry-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Insights", "18/08/20", contextualInquiryTemplates, holtzblattCreator)}
              <div className="tool-opening">
                <p>
                Contextual Inquiry is a field research method that involves observing and interviewing users in their natural environment as they perform tasks relevant to your research interests.
                </p>
                
                <p>
                Unlike traditional interviews, contextual inquiry focuses on observing actual behaviors rather than relying solely on self-reported information. This helps uncover tacit knowledge and workarounds that users may not explicitly mention.
                </p>
                
                <p>
                This method follows a master-apprentice model where the researcher acts as an apprentice learning from the user (the master) about how they perform tasks and solve problems in their actual context.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/contextual-inquiry/" className="link-table">When to Use Which User Experience Research Methods</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/contextual-interviews-and-how-to-handle-them" className="link-table">Contextual Interviews and How to Handle Them</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-contextual-inquiry" 
            id="tab-contextual-inquiry-template"
          />
          <label htmlFor="tab-contextual-inquiry-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_klP8qr0=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Contextual Inquiry Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Card Sorting Section */}
      <main className="case-study">
        <span className="case-tool-title" id="card-sorting">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="card-sorting">
            <span className="tool-title-number">
              <Link to="/empathise/user-insights#card-sorting">Card Sorting</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-insights">User Insights</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-insights#card-sorting"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-card-sorting" 
            id="tab-card-sorting-overview" 
            defaultChecked
          />
          <label htmlFor="tab-card-sorting-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Insights", "18/08/20", cardSortingTemplates, donaFarrellCreator)}
              <div className="tool-opening">
                <p>
                Card sorting is a technique where users organize content items into groups and label these groups in ways that make sense to them. This helps understand users' mental models and how they categorize information.
                </p>
                
                <p>
                There are two main types: open card sorting (where users create their own categories) and closed card sorting (where categories are predefined). Both provide insights into how to structure information architecture.
                </p>
                
                <p>
                This method is particularly useful for designing navigation systems, menu structures, and content organization that align with users' expectations.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/card-sorting-definition/" className="link-table">Card Sorting: Uncover Users' Mental Models for Better Information Architecture</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.usability.gov/how-to-and-tools/methods/card-sorting.html" className="link-table">Card Sorting - Usability.gov</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.optimalworkshop.com/learn/101s/card-sorting/" className="link-table">Card Sorting 101</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-card-sorting" 
            id="tab-card-sorting-template"
          />
          <label htmlFor="tab-card-sorting-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_klRh6OU=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Card Sorting Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserInsightsPage; 